import type { FlashcardDeckResponse, FlashcardsTodayDeck } from "@edu/shared";
import { cleanupPlan, flashcardsDbName, foreignFlashcardsDbs, type QueuedReview } from "./review-queue.ts";

/**
 * IndexedDB карточек: очередь повторений и кэш колод (7.2
 * `docs/flashcards-design.md`). База на пользователя, `edu-flashcards-<userId>`.
 *
 * ⚠️ **Стартовый чанк**, как и `review-queue.ts`: сюда ходит досылка при выходе.
 * Голый IndexedDB без обёрток, по образцу `media/resume-store.ts`.
 *
 * Каждое обращение в `try/catch`, и неудача это `null` или `false`, а не
 * исключение: в приватном окне хранилища нет вовсе, и тренировка обязана
 * работать без него, пока открыта вкладка. Отказ хранилища экран показывает
 * строкой сам, по возврату `false`.
 */

export type RecipientDeck = Extract<FlashcardDeckResponse, { view: "recipient" }>;

/** Кэш колоды: ответ получателю, его `rev` и начало запроса (`reviewsForProjection`). */
export interface DeckRecord {
  id: string;
  rev: number;
  fetchedAt: number;
  deck: RecipientDeck;
}

/**
 * Версия 2: третье хранилище `meta` с последним `today`. Без него колоды без
 * сети брались бы списком всего кэша, включая закрытые с тех пор.
 */
const VERSION = 2;
const QUEUE = "queue";
const DECKS = "decks";
const META = "meta";

const opening = new Map<string, Promise<IDBDatabase | null>>();

function openByName(name: string): Promise<IDBDatabase | null> {
  const known = opening.get(name);
  if (known !== undefined) return known;
  const p = new Promise<IDBDatabase | null>((resolve) => {
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(name, VERSION);
    } catch {
      resolve(null);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(QUEUE)) db.createObjectStore(QUEUE, { keyPath: "id" });
      if (!db.objectStoreNames.contains(DECKS)) db.createObjectStore(DECKS, { keyPath: "id" });
      if (!db.objectStoreNames.contains(META)) db.createObjectStore(META, { keyPath: "id" });
    };
    req.onsuccess = () => {
      const db = req.result;
      // Удаление базы из другой вкладки: отдаём соединение, иначе оно повиснет
      db.onversionchange = () => {
        db.close();
        opening.delete(name);
      };
      resolve(db);
    };
    // Неудача не запоминается: следующее обращение попробует открыть заново
    // (хранилище бывает занято соседней вкладкой со старой версией базы)
    const fail = () => {
      opening.delete(name);
      resolve(null);
    };
    req.onerror = fail;
    // `blocked` это не отказ хранилища, а соседняя вкладка, которая держит базу
    // старой версии и не отпускает её. Ждать её бессмысленно (она может не
    // закрыться никогда), поэтому сейчас работаем без хранилища, а следующее
    // обращение попробует снова: неудача не запоминается
    req.onblocked = fail;
  });
  opening.set(name, p);
  return p;
}

function run<T>(
  name: string,
  store: string,
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T> | null,
): Promise<{ ok: true; value: T | undefined } | { ok: false }> {
  return openByName(name).then(
    (db) =>
      new Promise((resolve) => {
        if (db === null) {
          resolve({ ok: false });
          return;
        }
        try {
          const tx = db.transaction(store, mode);
          const req = fn(tx.objectStore(store));
          let value: T | undefined;
          if (req !== null) req.onsuccess = () => (value = req.result);
          tx.oncomplete = () => resolve({ ok: true, value });
          tx.onerror = () => resolve({ ok: false });
          tx.onabort = () => resolve({ ok: false });
        } catch {
          resolve({ ok: false });
        }
      }),
  );
}

/** Вся очередь пользователя; `null`, если хранилище недоступно. */
export async function readQueue(userId: string): Promise<QueuedReview[] | null> {
  const r = await run<QueuedReview[]>(flashcardsDbName(userId), QUEUE, "readonly", (s) => s.getAll());
  return r.ok ? (r.value ?? []) : null;
}

/** Положить или обновить строки одной транзакцией. */
export async function putReviews(userId: string, rows: readonly QueuedReview[]): Promise<boolean> {
  if (rows.length === 0) return true;
  const r = await run(flashcardsDbName(userId), QUEUE, "readwrite", (s) => {
    for (const row of rows) s.put(row);
    return null;
  });
  return r.ok;
}

export async function deleteReviews(userId: string, ids: readonly string[]): Promise<boolean> {
  if (ids.length === 0) return true;
  const r = await run(flashcardsDbName(userId), QUEUE, "readwrite", (s) => {
    for (const id of ids) s.delete(id);
    return null;
  });
  return r.ok;
}

export async function readDecks(userId: string): Promise<DeckRecord[] | null> {
  const r = await run<DeckRecord[]>(flashcardsDbName(userId), DECKS, "readonly", (s) => s.getAll());
  return r.ok ? (r.value ?? []) : null;
}

export async function putDeck(userId: string, record: DeckRecord): Promise<boolean> {
  return (await run(flashcardsDbName(userId), DECKS, "readwrite", (s) => s.put(record))).ok;
}

/** Последний пришедший `today`: какие колоды открыты, на случай без сети. */
export async function readToday(userId: string): Promise<FlashcardsTodayDeck[] | null> {
  const r = await run<{ id: string; decks: FlashcardsTodayDeck[] }>(flashcardsDbName(userId), META, "readonly", (s) =>
    s.get("today"),
  );
  return r.ok && r.value !== undefined ? r.value.decks : null;
}

export async function putToday(userId: string, decks: readonly FlashcardsTodayDeck[]): Promise<boolean> {
  return (await run(flashcardsDbName(userId), META, "readwrite", (s) => s.put({ id: "today", decks }))).ok;
}

/** Есть ли база вообще. `null`, если браузер не умеет перечислять базы. */
export async function listDbNames(): Promise<(string | undefined)[] | null> {
  if (typeof indexedDB === "undefined" || typeof indexedDB.databases !== "function") return null;
  try {
    return (await indexedDB.databases()).map((d) => d.name);
  } catch {
    return null;
  }
}

/**
 * Уборка одной базы по `cleanupPlan`: кэш колод и доставленные строки стираются,
 * недоставленная очередь остаётся. База удаляется, только когда в ней пусто.
 */
async function cleanupByName(name: string): Promise<void> {
  const q = await run<QueuedReview[]>(name, QUEUE, "readonly", (s) => s.getAll());
  if (!q.ok) return;
  const plan = cleanupPlan(q.value ?? []);
  await run(name, DECKS, "readwrite", (s) => s.clear());
  await run(name, META, "readwrite", (s) => s.clear());
  await run(name, QUEUE, "readwrite", (s) => {
    for (const id of plan.dropIds) s.delete(id);
    return null;
  });
  if (plan.deleteDb) await dropDb(name);
}

async function dropDb(name: string): Promise<void> {
  const db = await opening.get(name);
  db?.close();
  opening.delete(name);
  try {
    // Открытая где-то ещё база повиснет в blocked; следующий заход уберёт её
    indexedDB.deleteDatabase(name);
  } catch {
    /* хранилище недоступно: уборка не критична */
  }
}

/**
 * Перенос заглушки (9.7 `docs/account-adopt-design.md`): этот браузер подтвердил
 * перенос, и заглушкой стал владелец его сессии, то есть `userId` сменился.
 * База `edu-flashcards-<старый>` переписывается под новое имя целиком
 * (очередь, кэш колод, последний `today`), старая удаляется.
 *
 * ⚠️ Старая удаляется, только если ВСЕ записи в новую прошли: иначе
 * недоставленная очередь осталась бы только в базе, которую никто больше не
 * читает. Неудача не мешает входу: строки лежат под старым именем, как на
 * втором устройстве (принятая цена 7.4).
 *
 * ⚠️ `meta` выжившего не перезаписывается: если у него в этом браузере уже была
 * своя база, его последний `today` свежее записи заглушки. Очередь и кэш колод
 * пишутся `put` поверх: id повторений это uuid, а колоды у двух учеников
 * разные.
 *
 * Базы, которой нет, не трогаем: открытие по имени создало бы её пустой.
 * Браузер, не умеющий перечислять базы, всё равно идёт по общему пути: пустая
 * база создастся и тут же удалится.
 */
export function moveFlashcardsDb(oldId: string, newId: string): Promise<boolean> {
  if (oldId === newId) return Promise.resolve(true);
  return moveDbWith(
    {
      list: listDbNames,
      read: async (name, store) => {
        const r = await run<unknown[]>(name, store, "readonly", (s) => s.getAll());
        return r.ok ? (r.value ?? []) : null;
      },
      write: async (name, store, values) =>
        (
          await run(name, store, "readwrite", (s) => {
            for (const v of values) s.put(v);
            return null;
          })
        ).ok,
      drop: dropDb,
    },
    flashcardsDbName(oldId),
    flashcardsDbName(newId),
  );
}

/** Доступ к базам для `moveDbWith`. Интерфейс, чтобы тест дал своё хранилище. */
export interface DbIo {
  list(): Promise<(string | undefined)[] | null>;
  /** Все записи хранилища; `null`, если база недоступна. */
  read(name: string, store: string): Promise<unknown[] | null>;
  write(name: string, store: string, values: readonly unknown[]): Promise<boolean>;
  drop(name: string): Promise<void>;
}

/** Правила переноса базы `from` в `to`, без IndexedDB (тест в `review-store.test.ts`). */
export async function moveDbWith(io: DbIo, from: string, to: string): Promise<boolean> {
  const names = await io.list();
  if (names !== null && !names.includes(from)) return true;
  const rows: { store: string; values: unknown[] }[] = [];
  for (const store of [QUEUE, DECKS, META]) {
    const values = await io.read(from, store);
    if (values === null) return false;
    rows.push({ store, values });
  }
  for (const { store, values } of rows) {
    if (values.length === 0) continue;
    if (store === META) {
      const own = await io.read(to, META);
      if (own === null) return false;
      if (own.length > 0) continue;
    }
    if (!(await io.write(to, store, values))) return false;
  }
  await io.drop(from);
  return true;
}

/** Выход (7.4): стирается кэш колод этого человека, очередь остаётся. */
export function forgetDecks(userId: string): Promise<void> {
  return cleanupByName(flashcardsDbName(userId));
}

/**
 * Вход (7.4): кэш колод ДРУГИХ людей этого браузера стирается, их недоставленная
 * очередь остаётся до их следующего входа (15.11, принятый риск).
 */
export async function pruneForeignFlashcards(userId: string): Promise<void> {
  const names = await listDbNames();
  if (names === null) return;
  for (const name of foreignFlashcardsDbs(names, userId)) await cleanupByName(name);
}
